import { Flex } from 'mds-web-ui'
import { graphql, useStaticQuery } from 'gatsby'
import { capitalize } from 'lodash'
import React, { useMemo } from 'react'

import { ScrollTop } from '../TopNav/ScrollTop'
import { processImageData } from '../utils/processImageData'
import { CategorySelect } from './CategorySelect'
import { FlowGalleryHeader } from './FlowGalleryHeader.jsx'
import { MasonryGrid } from './MasonryGrid'
import { ShareWithUs } from './ShareWithUs'
import { TryFlow } from './TryFlow'
import { useAppIcons } from '../utils/useAppIcons'
import { ImageCount } from './ImageCount'
import { Tag } from './Tag'
import { Meta } from '../shared/Meta'
import { SharedLayout } from '../shared/SharedLayout'

const useFlowGalleryImages = imageData => {
  const processedImageData = useMemo(() => {
    return processImageData(imageData)
  }, [imageData])

  return processedImageData
}

const useFlowMetaImages = () => {
  const data = useStaticQuery(graphql`
    query FlowGalleryImages {
      flowCTAImage: file(relativePath: { eq: "flow/flow_cta_wordmark.png" }) {
        childImageSharp {
          ...standardImage
        }
      }
      flowSEOImage: file(relativePath: { eq: "flow/flow-ipad.jpg" }) {
        childImageSharp {
          ...thumbnail
        }
      }
    }
  `)

  const { flowSEOImage, flowCTAImage } = data
  return {
    seoImage: flowSEOImage.childImageSharp.resize,
    ctaImage: flowCTAImage
  }
}

export const FlowGallery = ({
  slug,
  totalCount,
  tag,
  categories,
  category,
  imageData,
  children,
  ...props
}) => {
  const { allImages } = useFlowGalleryImages(imageData)
  const { seoImage, ctaImage } = useFlowMetaImages()
  const appIcons = useAppIcons()

  const title = `${category ? `${capitalize(category)} - ` : ''}Flow Gallery`

  return (
    <>
      <SharedLayout appIcons={appIcons} position="sticky">
        <Flex
          {...props}
          as="main"
          pt={[4, 48]}
          pb={[4, 6]}
          px={[3, 4, 4]}
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <Flex flexDirection="column" gap={[4, 48]} flex="1">
            <FlowGalleryHeader>
              {categories ? (
                <CategorySelect categories={categories} category={category} />
              ) : null}
              {tag ? (
                <Flex alignItems="center" justifyContent="center" gap={3}>
                  <Tag to={'/flow/gallery'}>{`#${tag} ×`}</Tag>
                  <ImageCount>{totalCount}</ImageCount>
                </Flex>
              ) : null}
            </FlowGalleryHeader>

            {children}

            <MasonryGrid imageData={allImages} />

            <ShareWithUs />
            <TryFlow ctaImage={ctaImage} appIcons={appIcons} />

            <ScrollTop appIcons={appIcons} showBundleLink />
          </Flex>
        </Flex>
      </SharedLayout>

      <Meta
        seoImage={seoImage}
        path={slug}
        title={title}
        description="Artwork we love from Flow shared with the world."
        noindex
      />
    </>
  )
}
